import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppStore } from "../../store/useAppStore";
import { Button } from "../../components/ui/Button";
import { PassAttnFail } from "../../components/ui/PassAttnFail";
import { SyncBadge } from "../../components/ui/SyncBadge";
import { JarvisPanel } from "../../components/situations/JarvisPanel";
import type { InspectionItemResult } from "../../domain/types";
import { InspectionReview } from "./InspectionReview";

export function InspectionFlow() {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const inspection = useAppStore((s) => s.inspections.find((i) => i.id === inspectionId));
  const startInspection = useAppStore((s) => s.startInspection);
  const pauseInspection = useAppStore((s) => s.pauseInspection);
  const recordInspectionItem = useAppStore((s) => s.recordInspectionItem);
  const setInspectionStep = useAppStore((s) => s.setInspectionStep);
  const completeInspection = useAppStore((s) => s.completeInspection);

  const [note, setNote] = useState("");
  const [photoAdded, setPhotoAdded] = useState(false);
  const [voiceCaptured, setVoiceCaptured] = useState(false);

  if (!inspection) return <p className="p-6 text-[13.5px] text-muted-foreground">Inspection not found.</p>;

  if (inspection.status === "review" || inspection.status === "completed") {
    return <InspectionReview inspectionId={inspection.id} />;
  }

  if (inspection.status === "not-started") {
    return (
      <div className="flex flex-1 flex-col justify-between p-5">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">Guided inspection · {inspection.items.length} steps</p>
          <h1 className="mt-2 font-display text-[26px] font-black leading-8 tracking-tight text-brand-navy">{inspection.title}</h1>
          <p className="mt-2 text-[14px] leading-6 text-muted-foreground">
            One task per screen. Your progress saves automatically as you go — you can pause and resume anytime, even
            offline.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="quiet" onClick={() => navigate(-1)} className="flex-1">
            Back
          </Button>
          <Button onClick={() => startInspection(inspection.id)} className="flex-[2]">
            Start inspection
          </Button>
        </div>
      </div>
    );
  }

  const item = inspection.items[inspection.currentStepIndex];
  const isLast = inspection.currentStepIndex === inspection.items.length - 1;
  const flaggedCount = inspection.items.filter((i) => i.result === "attention" || i.result === "fail").length;

  function selectResult(result: Exclude<InspectionItemResult, null>) {
    recordInspectionItem(inspection!.id, item.id, result, {
      note: note || undefined,
      photoId: photoAdded ? `photo-${item.id}` : undefined,
      voiceTranscript: voiceCaptured ? "Wall damage worse than last visit." : undefined
    });
  }

  function goNext() {
    setNote("");
    setPhotoAdded(false);
    setVoiceCaptured(false);
    if (isLast) {
      completeInspection(inspection!.id);
    } else {
      setInspectionStep(inspection!.id, inspection!.currentStepIndex + 1);
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="border-b-2 border-brand-navy px-5 pb-3.5 pt-4">
        <div className="flex items-center justify-between">
          <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
            {inspection.title.split("—")[0]}
          </p>
          <div className="flex items-center gap-3">
            <SyncBadge />
            <Button variant="ghost" className="min-h-[34px] px-3 text-[12px]" onClick={() => pauseInspection(inspection.id)}>
              Pause
            </Button>
          </div>
        </div>
        <div className="mt-2.5 flex gap-1">
          {inspection.items.map((it, i) => (
            <span
              key={it.id}
              className={`h-[3px] flex-1 rounded-full ${
                it.result === "fail"
                  ? "bg-danger"
                  : it.result === "attention"
                  ? "bg-attention"
                  : it.result === "pass"
                  ? "bg-success"
                  : i === inspection.currentStepIndex
                  ? "bg-primary"
                  : "bg-border"
              }`}
            />
          ))}
        </div>
        <div className="mt-1.5 flex justify-between font-mono text-[10.5px] text-muted-foreground">
          <span>
            STEP {inspection.currentStepIndex + 1} / {inspection.items.length}
          </span>
          {flaggedCount > 0 && <span className="text-attention">{flaggedCount} FLAGGED</span>}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 p-5">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{item.category}</p>
          <h2 className="mt-1 font-display text-[26px] font-black leading-8 tracking-tight text-foreground">{item.title}</h2>
          <p className="mt-2 max-w-sm text-[14px] leading-5 text-muted-foreground">{item.hint}</p>
        </div>

        <PassAttnFail value={item.result} onChange={selectResult} />

        {item.result && (item.result === "attention" || item.result === "fail") && (
          <JarvisPanel>
            <p>Flagged {item.result}. A draft follow-up action will be created for your review at the end of this walk.</p>
          </JarvisPanel>
        )}

        <div className="flex gap-2">
          <Button variant="quiet" onClick={() => setPhotoAdded(true)} aria-pressed={photoAdded} className="flex-1">
            {photoAdded ? "Photo added ✓" : "Add photo"}
          </Button>
          <Button variant="quiet" onClick={() => setVoiceCaptured(true)} aria-pressed={voiceCaptured} className="flex-1">
            {voiceCaptured ? "Voice note ✓" : "Voice note"}
          </Button>
        </div>

        <label className="block">
          <span className="mb-1 block font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground">Note (optional)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full rounded-sm border border-border bg-card p-2.5 text-[13.5px] text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            placeholder="Add a note for this step…"
          />
        </label>

        <div className="mt-auto flex items-center justify-between rounded-sm border border-border bg-card px-3.5 py-2.5">
          <span className="font-mono text-[10.5px] text-muted-foreground">
            {item.result ? "Step saved on device" : "Select a result to continue"}
          </span>
        </div>

        <div className="flex gap-2">
          <Button
            variant="quiet"
            className="flex-1"
            onClick={() => inspection.currentStepIndex > 0 && setInspectionStep(inspection.id, inspection.currentStepIndex - 1)}
            disabled={inspection.currentStepIndex === 0}
          >
            Back
          </Button>
          <Button className="flex-[2]" onClick={goNext} disabled={!item.result}>
            {isLast ? "Finish · Review actions" : `Next · ${inspection.items[inspection.currentStepIndex + 1]?.title ?? ""}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
